import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { orderAPI } from '../../services/subscriptionApi';
import { useAuth } from '../../contexts/AuthContext';

interface Plan {
  plan: string;
  name: string;
  price: number;
  billing: string;
  duration: number;
  features: string[];
  badge?: string;
}

interface CurrentSubscription {
  plan: string;
  status: string;
  expiresAt: string;
  daysRemaining: number;
  isActive: boolean;
  isPremium: boolean;
  features: string[];
}

interface BillingHistory {
  id: number;
  date: string;
  plan: string;
  planName: string;
  amount: number;
  currency: string;
  status: string;
}

// Maps plan string keys to numeric plan IDs used by the backend
const PLAN_ID_MAP: Record<string, number> = {
  FREE: 1,
  MONTHLY: 2,
  ANNUAL: 3,
};

const SubscriptionPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentSub, setCurrentSub] = useState<CurrentSubscription | null>(null);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [billingHistory, setBillingHistory] = useState<BillingHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subRes, plansRes, historyRes] = await Promise.all([
        api.get('/subscription/me'),
        api.get('/subscription/plans'),
        api.get('/subscription/billing-history'),
      ]);

      setCurrentSub(subRes.data.data);
      setPlans(plansRes.data.data);
      setBillingHistory(historyRes.data.data);
    } catch (error) {
      console.error('Failed to fetch subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async (plan: string) => {
    if (upgrading) return;

    const planId = PLAN_ID_MAP[plan];
    if (!planId || !user) return;

    setUpgrading(true);
    try {
      const response = await orderAPI.createOrder({ planId, userId: user.id });
      const { orderId } = response.data;
      navigate(`/order/${orderId}`);
    } catch (error: any) {
      console.error('Order creation error:', error);
      // Handle duplicate pending order — navigate to the existing order
      const existingOrderId = error?.response?.data?.data?.existingOrderId;
      if (existingOrderId) {
        navigate(`/order/${existingOrderId}`);
        return;
      }
      alert(error.message || 'Failed to create order. Please try again.');
    } finally {
      setUpgrading(false);
    }
  };

  const getPlanColor = (plan: string) => {
    const colors: Record<string, string> = {
      FREE: 'border-gray-300 bg-white',
      MONTHLY: 'border-blue-500 bg-blue-50',
      ANNUAL: 'border-purple-500 bg-purple-50',
    };
    return colors[plan] || 'border-gray-300 bg-white';
  };

  const getPlanBadgeColor = (plan: string) => {
    const colors: Record<string, string> = {
      FREE: 'bg-gray-100 text-gray-800',
      MONTHLY: 'bg-blue-100 text-blue-800',
      ANNUAL: 'bg-purple-100 text-purple-800',
    };
    return colors[plan] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading subscription...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Subscription</h1>
        <p className="text-gray-600 mt-2">Manage your subscription and billing</p>
      </div>

      {/* Current Plan Card */}
      {currentSub && (
        <div className={`rounded-lg border-2 p-6 mb-8 ${getPlanColor(currentSub.plan)}`}>
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-2xl font-bold text-gray-900">
                  {plans.find(p => p.plan === currentSub.plan)?.name || currentSub.plan}
                </h2>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPlanBadgeColor(currentSub.plan)}`}>
                  {currentSub.status === 'active' ? '✓ Active' : currentSub.status}
                </span>
              </div>
              
              {currentSub.plan === 'FREE' ? (
                <div className="bg-gradient-to-r from-blue-500 to-purple-600 text-white p-4 rounded-lg mt-4">
                  <p className="text-lg font-semibold">
                    🚀 You are currently on Free Plan — Unlock full mock tests and AI analysis with Pro!
                  </p>
                </div>
              ) : (
                <div className="mt-4">
                  <p className="text-gray-700">
                    <span className="font-semibold">Expires:</span>{' '}
                    {new Date(currentSub.expiresAt).toLocaleDateString('en-IN', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                  <p className="text-gray-700 mt-1">
                    <span className="font-semibold">Days Remaining:</span> {currentSub.daysRemaining} days
                  </p>
                </div>
              )}

              <div className="mt-4">
                <p className="text-sm font-semibold text-gray-700 mb-2">Features Unlocked:</p>
                <ul className="space-y-1">
                  {currentSub.features.slice(0, 3).map((feature, index) => (
                    <li key={index} className="text-sm text-gray-600 flex items-center">
                      <span className="mr-2">{currentSub.plan === 'FREE' ? '❌' : '✓'}</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Nudges for Free Users */}
      {currentSub?.plan === 'FREE' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="text-3xl mb-2">🚀</div>
            <h3 className="font-semibold text-gray-900 mb-1">Smart Practice</h3>
            <p className="text-sm text-gray-600">AI-powered personalized practice</p>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="text-3xl mb-2">📊</div>
            <h3 className="font-semibold text-gray-900 mb-1">Deep Insights</h3>
            <p className="text-sm text-gray-600">Advanced performance analytics</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-3xl mb-2">🎯</div>
            <h3 className="font-semibold text-gray-900 mb-1">Rank Prediction</h3>
            <p className="text-sm text-gray-600">AI-based JEE rank estimation</p>
          </div>
        </div>
      )}

      {/* Plan Comparison */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Choose Your Plan</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <div
              key={plan.plan}
              className={`rounded-lg border-2 p-6 relative ${
                plan.plan === currentSub?.plan
                  ? 'border-blue-500 bg-blue-50'
                  : 'border-gray-200 bg-white hover:border-blue-300 transition-colors'
              }`}
            >
              {plan.badge && (
                <div className="absolute top-0 right-0 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-3 py-1 rounded-bl-lg rounded-tr-lg text-sm font-semibold">
                  {plan.badge}
                </div>
              )}

              <div className="mb-4">
                <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                <div className="mt-2">
                  <span className="text-4xl font-bold text-gray-900">₹{plan.price}</span>
                  {plan.price > 0 && <span className="text-gray-600 ml-2">/ {plan.billing.toLowerCase()}</span>}
                </div>
                <p className="text-sm text-gray-600 mt-1">{plan.billing}</p>
              </div>

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start text-sm text-gray-700">
                    <span className="mr-2 mt-0.5">{plan.plan === 'FREE' ? '○' : '✓'}</span>
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => plan.plan !== 'FREE' && plan.plan !== currentSub?.plan && handleUpgrade(plan.plan)}
                disabled={plan.plan === currentSub?.plan || upgrading}
                className={`w-full py-3 rounded-lg font-semibold transition-colors ${
                  plan.plan === currentSub?.plan
                    ? 'bg-gray-200 text-gray-600 cursor-not-allowed'
                    : plan.plan === 'FREE'
                    ? 'bg-gray-300 text-gray-700 cursor-not-allowed'
                    : plan.plan === 'ANNUAL'
                    ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {plan.plan === currentSub?.plan
                  ? 'Current Plan'
                  : plan.plan === 'FREE'
                  ? 'Free Forever'
                  : upgrading
                  ? 'Processing...'
                  : `Upgrade to ${plan.name}`}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Billing History */}
      {billingHistory.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Billing History</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Date</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Plan</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Amount</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Status</th>
                </tr>
              </thead>
              <tbody>
                {billingHistory.map((item) => (
                  <tr key={item.id} className="border-b hover:bg-gray-50">
                    <td className="py-3 px-4 text-sm text-gray-900">
                      {new Date(item.date).toLocaleDateString('en-IN')}
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-900">{item.planName}</td>
                    <td className="py-3 px-4 text-sm text-gray-900">
                      ₹{item.amount} {item.currency}
                    </td>
                    <td className="py-3 px-4">
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                        {item.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPage;
